import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import {
	IImpersonationContextComponentProps,
	IModalContext,
	ImpersonationContextKey,
	InternalLevitateAccountIds,
	ModalChildComponentContextKey,
} from '../../../../models';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getIndustryName } from '../../../../models/UiUtils';
import { useGetNewsletterTemplatesByKeyword, useTemplatesByType } from '../../../../queries';
import { useGetTemplatesByIndustry } from '../../../../queries/Templates/useGetTemplatesByIndustry';
import {
	ImpersonationBroker,
	Industry,
	ITemplate,
	ITemplateCard,
	TemplateType,
} from '../../../../viewmodels/AppViewModels';
import { bs } from '../../../styles/styles';
import { CampaignCalendarTemplateCard } from '../../campaigns/CampaignsByCategorySelector/presentation';
import { CampaignSearchInputField } from '../../campaigns/CampaignSearchInputField';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { LoadingSpinner } from '../../LoadingSpinner';
import { asModalComponent } from '../../Modal';
import { styleSheet } from './styles';

interface Props extends IImpersonationContextComponentProps, IModalContext<ITemplate> {
	title: React.ReactNode;
}

function _NewsletterTemplateBrowser(props: Props) {
	const { impersonationContext } = props;
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('CampaignSearch');
	const account = impersonationContext?.isValid ? impersonationContext.account : userSession?.account;
	const [industry, setIndustry] = React.useState(account?.additionalInfo?.industry ?? Industry.Insurance);
	const [searchQuery, setSearchQuery] = React.useState('');
	const debouncedKeywordsSearchValue = useDebounceValue(searchQuery);
	const industries = React.useMemo(() => {
		const base = [
			Industry.Insurance,
			Industry.RealEstate,
			Industry.Financial,
			Industry.Mortgage,
			Industry.Legal,
			Industry.Accounting,
			Industry.NonProfit,
			Industry.HomeMaintenance,
			Industry.Dental,
			Industry.Chiropractic,
			Industry.Others,
		];
		return InternalLevitateAccountIds.has(impersonationContext?.account?.id ?? userSession.account.id)
			? [...base, Industry.Levitate]
			: base;
	}, [impersonationContext?.account?.id, userSession.account.id]);
	const filteredNewslettersQuery = useGetNewsletterTemplatesByKeyword({
		enabled: !!debouncedKeywordsSearchValue,
		industry,
		impersonationContext: userSession.impersonationContext,
		meta: {
			errorLogging: {
				category: 'NewsletterTemplateBrowser',
				action: 'FilterNewsletterByKeyword-Error',
			},
		},
		search: debouncedKeywordsSearchValue,
	});
	const industryTemplates = useGetTemplatesByIndustry({
		enabled: !debouncedKeywordsSearchValue, // Disable when searching
		impersonationContext: userSession.impersonationContext,
		industry,
		types: [TemplateType.HtmlNewsletter],
		meta: {
			errorLogging: {
				category: 'NewsletterTemplateBrowser',
				action: 'GetIndustryTemplates-Error',
			},
		},
	});
	const templatesQuery = useTemplatesByType({
		enabled: !debouncedKeywordsSearchValue, // Disable when searching
		impersonationContext: userSession.impersonationContext,
		includeNonUserTemplates: true,
		type: TemplateType.HtmlNewsletter,
	});
	const onIndustryChanged = (value: string) => {
		setIndustry(value as Industry);
	};
	const onCampaignTemplateCardClicked = async (card: ITemplateCard, socialIndicator?: boolean) => {
		const templateId = socialIndicator
			? card.associatedTemplates?.find(x => x.templateType === TemplateType.SocialMediaPost)?.id
			: card.id;
		if (templateId) {
			try {
				const template = await userSession.webServiceHelper.callAsync<ITemplate>(
					ImpersonationBroker.composeApiUrl({
						impersonationContext,
						urlPath: `template/${encodeURIComponent(templateId)}`,
						queryParams: {
							expand: 'LastUsedByDate',
						},
					}),
					'GET'
				);
				props.parentModal?.onRequestClose(template, !template);
			} catch (error) {
				errorMessages.pushApiError(error);
				logger.logApiError('TemplateLoad-Error', error);
			}
		}
	};
	const onCampaignSearch = (query: string) => {
		setSearchQuery(query);
	};
	let isLoading = false;
	if (debouncedKeywordsSearchValue && !filteredNewslettersQuery.isSuccess) {
		isLoading = true;
	} else if (!industryTemplates.isSuccess || !templatesQuery.isSuccess) {
		isLoading = true;
	}
	const templates = React.useMemo(() => {
		let value: ITemplate[] = [];
		// When searching: use filtered results that include user templates automatically
		if (debouncedKeywordsSearchValue && filteredNewslettersQuery.data) {
			value = filteredNewslettersQuery.data;
		}
		// When not searching: combine industry templates with user templates and deduplicate
		else if (industryTemplates.data) {
			value = [...industryTemplates.data];

			// Add user templates, ensuring no duplicates by ID
			if (templatesQuery.data) {
				const userTemplatesList = templatesQuery.data.pages.flatMap(x => x.values);
				const existingIds = new Set(value.map(t => t.id));

				userTemplatesList.forEach(userTemplate => {
					if (!existingIds.has(userTemplate.id)) {
						value.push(userTemplate);
					}
				});
			}
		}
		return value;
	}, [filteredNewslettersQuery.data, industryTemplates.data, templatesQuery.data, debouncedKeywordsSearchValue]);
	return (
		<div className={`${css(styleSheet.container)}`}>
			<div className={css(styleSheet.header)}>
				{props.title || 'Create a new message template or pick from an existing one...'}
			</div>
			<div className={css(styleSheet.searchContainer)}>
				<CampaignSearchInputField
					onChange={onCampaignSearch}
					industry={industry}
					placeholderText='Search by keywords'
				/>
			</div>
			<div className={css(styleSheet.select)}>
				<Select value={industry} onValueChange={onIndustryChanged}>
					<SelectTrigger styles={styleSheet.trigger}>
						<span className={css(styleSheet.triggerText)}>{getIndustryName(industry)}</span>
					</SelectTrigger>
					<SelectContent>
						{industries.map(i => (
							<SelectItem key={i} value={i}>
								{getIndustryName(i)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			{isLoading ? <LoadingSpinner className={css(bs.absoluteCenter)} type='large' /> : null}
			{!isLoading ? (
				<div className={css(styleSheet.cardsContainer)}>
					{templates.map(templateCard => (
						<CampaignCalendarTemplateCard
							card={templateCard as ITemplateCard}
							className={css(styleSheet.templateCardContainer)}
							key={templateCard.id}
							impersonationContext={impersonationContext}
							onCtaClicked={() => onCampaignTemplateCardClicked(templateCard as ITemplateCard)}
							styles={[styleSheet.templateCard]}
						/>
					))}
				</div>
			) : null}
		</div>
	);
}

const NewsletterTemplateBrowserAsObserver = observer(_NewsletterTemplateBrowser);
export const NewsletterTemplateBrowser = inject(
	ModalChildComponentContextKey,
	ImpersonationContextKey
)(NewsletterTemplateBrowserAsObserver);

export const NewsletterTemplateBrowserDialog = asModalComponent(NewsletterTemplateBrowser, {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
});
